import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Eye, EyeOff, ExternalLink, Copy, Upload } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProducts = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchProducts();
  }, [token]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products?include_inactive=true&limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleDuplicate = async (productId) => {
    try {
      const res = await axios.post(`${API_URL}/api/products/${productId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Duplicated. New product is hidden until you publish it.`);
      // Open the new copy directly in the edit form so they can tweak it.
      window.location.href = `/admin/products/${res.data.id}/edit`;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate product');
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      
      <div data-testid="admin-products">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold">Products</h1>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/products/import"
              className="flex items-center px-4 py-2 bg-white border-2 border-[#4A90A4] text-[#4A90A4] font-medium rounded hover:bg-[#4A90A4]/5 transition-all"
              data-testid="import-products-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Link>
            <Link
              to="/admin/products/new"
              className="flex items-center px-4 py-2 bg-primary text-primary-foreground font-medium rounded hover:brightness-110 transition-all"
              data-testid="add-product-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Product
            </Link>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md px-4 py-3 pl-10 bg-input border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        </div>

        {/* Products Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground">Loading...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              No products found. <Link to="/admin/products/new" className="text-primary">Create one?</Link>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 text-sm font-medium">Product</th>
                  <th className="text-left p-4 text-sm font-medium">Price</th>
                  <th className="text-left p-4 text-sm font-medium">Status</th>
                  <th className="text-right p-4 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="border-b border-border hover:bg-accent/50">
                    <td className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-muted rounded overflow-hidden flex-shrink-0">
                          {product.primary_image ? (
                            <img 
                              src={product.primary_image} 
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                              No img
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-sm text-muted-foreground">{product.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      {product.sale_price ? (
                        <div>
                          <span className="font-medium text-primary">${product.sale_price.toFixed(2)}</span>
                          <span className="ml-2 text-sm text-muted-foreground line-through">
                            ${product.base_price.toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        <span className="font-medium">${product.base_price.toFixed(2)}</span>
                      )}
                    </td>
                    <td className="p-4">
                      {product.is_active ? (
                        <span className="flex items-center text-green-500 text-sm">
                          <Eye className="w-4 h-4 mr-1" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center text-muted-foreground text-sm">
                          <EyeOff className="w-4 h-4 mr-1" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {product.is_active && <Link to={`/product/${product.slug}`} target="_blank" className="text-sm underline px-2 py-3 whitespace-nowrap">View Product</Link>}
                        <Link to={`/admin/products/${product.id}/preview`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-accent rounded" title={`Preview ${product.name}`} aria-label={`Preview ${product.name}`}><ExternalLink className="w-4 h-4" /></Link>
                        <Link
                          to={`/admin/products/${product.id}/edit`}
                          className="p-2 hover:bg-accent rounded transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDuplicate(product.id)}
                          className="p-2 hover:bg-accent rounded transition-colors"
                          title="Duplicate"
                          data-testid={`duplicate-product-${product.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="p-2 hover:bg-destructive/10 hover:text-destructive rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
